"use client";

import { tsv } from "@faire/slate/lib/tsv";
import {
  FloatingArrow,
  FloatingPortal,
  FloatingFocusManager,
  useMergeRefs,
  useTransitionStyles,
} from "@floating-ui/react";
import React from "react";

import {
  POPOVER_ARROW_HEIGHT,
  POPOVER_ARROW_WIDTH,
  POPOVER_ARROW_TIP_RADIUS,
  POPOVER_OPEN_TRANSITION_DURATION_MS,
  POPOVER_CLOSE_TRANSITION_DURATION_MS,
} from "./consts";
import { usePopoverContext } from "./PopoverContext";

interface IProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * Whether the popover content is focusable.
   *
   * @default false
   */
  focusable?: boolean;
  /**
   * Whether to use a portal to render the popover.
   */
  portaled?: boolean;
}

export const PopoverContent: React.FC<IProps> = ({ portaled, ...props }) => {
  const { context } = usePopoverContext();
  const { isMounted } = useTransitionStyles(context);

  if (!isMounted) {
    return null;
  }

  if (portaled) {
    return (
      <FloatingPortal>
        <TooltipBase {...props} />
      </FloatingPortal>
    );
  }

  return <TooltipBase {...props} />;
};

const TooltipBase: React.FC<Omit<IProps, "usePortal">> = ({
  children,
  className,
  ref: propRef,
  style,
  focusable,
  ...props
}) => {
  const {
    arrowRef,
    context,
    refs,
    floatingStyles,
    getFloatingProps,
    middlewareData,
  } = usePopoverContext();
  const ref = useMergeRefs([refs.setFloating, propRef]);
  const { styles: transitionStyles } = useTransitionStyles(context, {
    duration: {
      open: POPOVER_OPEN_TRANSITION_DURATION_MS,
      close: POPOVER_CLOSE_TRANSITION_DURATION_MS,
    },
  });

  const tooltipCls = popoverStyle.cls({ className });

  const arrowStyle = {
    ...(middlewareData.arrow?.x ? { left: middlewareData.arrow?.x } : {}),
    ...(middlewareData.arrow?.y ? { top: middlewareData.arrow?.y } : {}),
  };

  return (
    <FloatingFocusManager
      context={context}
      disabled={!focusable}
      modal={focusable}
    >
      <div
        {...props}
        className={tooltipCls}
        ref={ref}
        style={{
          ...floatingStyles,
          ...transitionStyles,
          ...style,
          ...popoverVars,
        }}
        {...getFloatingProps(props)}
      >
        {children}
        <FloatingArrow
          ref={arrowRef}
          context={context}
          className="fs-popover__arrow"
          width={POPOVER_ARROW_WIDTH}
          height={POPOVER_ARROW_HEIGHT}
          tipRadius={POPOVER_ARROW_TIP_RADIUS}
          style={arrowStyle}
        />
      </div>
    </FloatingFocusManager>
  );
};

const popoverStyle = tsv({
  vars: {
    zIndex: "fs-popover-z-index",
    padding: "fs-popover-padding",
  },
  base: "fs-popover",
});

export const popoverVars = popoverStyle.vars;
